import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronUp, Layers, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import PDFSlideViewer from './PDFSlideViewer';

const SwipeUpPanel = ({ slide, presentation, isFullscreen = false, externalOpen = false, onClose = null }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTabVisible, setIsTabVisible] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [openedByDrag, setOpenedByDrag] = useState(false);
  const hideTimerRef = useRef(null);
  const panelRef = useRef(null);
  const tabRef = useRef(null);
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const dragResetTimerRef = useRef(null);

  // Handle external open state (from slideshow button)
  useEffect(() => {
    if (externalOpen && !isExpanded) {
      setIsExpanded(true);
      setOpenedByDrag(false);
    }
  }, [externalOpen]);

  // Auto-hide functionality - More forgiving on mobile
  const resetHideTimer = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
    
    setIsTabVisible(true);
    
    // Auto-hide after 5 seconds if not expanded (increased from 3s for better mobile UX)
    if (!isExpanded) {
      hideTimerRef.current = setTimeout(() => {
        setIsTabVisible(false);
      }, 5000);
    }
  };

  // Detect if running on iOS Safari
  const isIOSSafari = () => {
    const ua = navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua);
    const webkit = /WebKit/.test(ua);
    const notChrome = !/CriOS|Chrome/.test(ua);
    return iOS && webkit && notChrome;
  };

  // Register touch events with passive: false for Safari
  useEffect(() => {
    const tabElement = tabRef.current;
    const headerElement = headerRef.current;
    const footerElement = footerRef.current;

    if (!tabElement && !headerElement && !footerElement) return;

    // Add touch listeners with passive: false to allow preventDefault
    const elements = [tabElement, headerElement, footerElement].filter(Boolean);
    
    elements.forEach(element => {
      if (element) {
        element.addEventListener('touchmove', onTouchMove, { passive: false });
      }
    });

    return () => {
      elements.forEach(element => {
        if (element) {
          element.removeEventListener('touchmove', onTouchMove);
        }
      });
    };
  }, [isExpanded, touchStart, dragOffset]);

  // Show tab on mouse move, touch, or scroll near bottom
  useEffect(() => {
    const isSafari = isIOSSafari();
    
    const handleActivity = () => {
      if (!isExpanded) {
        resetHideTimer();
      }
    };

    // Check if user scrolled near bottom of page
    const handleScroll = () => {
      if (isExpanded) return;
      
      const scrolledToBottom = (window.innerHeight + window.scrollY) >= document.documentElement.scrollHeight - 100;
      if (scrolledToBottom) {
        setIsTabVisible(true);
        resetHideTimer();
      }
    };

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('touchstart', handleActivity, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });

    // On iOS Safari, keep tab always visible or show floating button immediately
    if (isSafari) {
      setIsTabVisible(true);
      // Don't auto-hide on Safari - it causes too many issues
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    } else {
      // Initial timer for other browsers
      resetHideTimer();
    }

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('scroll', handleScroll);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      if (dragResetTimerRef.current) {
        clearTimeout(dragResetTimerRef.current);
      }
    };
  }, [isExpanded]);

  // Swipe detection with drag animation
  const minSwipeDistance = 50;
  const snapThreshold = 150; // Distance to trigger open/close

  const onTouchStart = (e) => {
    // Don't prevent default to allow scrolling when needed
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
    setIsDragging(true);
    
    // Keep tab visible during interaction
    setIsTabVisible(true);
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
  };

  const onTouchMove = (e) => {
    if (!touchStart) return;
    
    const currentTouch = e.targetTouches[0].clientY;
    setTouchEnd(currentTouch);
    
    const distance = touchStart - currentTouch;
    
    // For collapsed state: allow dragging up
    if (!isExpanded) {
      if (distance > 0) {
        // Prevent default scroll when dragging up the panel
        // Must be called before any state updates for Safari
        if (e.cancelable) {
          e.preventDefault();
        }
        // Dragging up - apply offset with resistance
        const offset = Math.min(distance, window.innerHeight);
        setDragOffset(offset);
      } else {
        setDragOffset(0);
      }
    } 
    // For expanded state: allow dragging down
    else {
      if (distance < 0) {
        // Prevent default scroll when dragging down the panel
        if (e.cancelable) {
          e.preventDefault();
        }
        // Dragging down - apply offset
        const offset = Math.max(distance, -window.innerHeight);
        setDragOffset(offset);
      } else {
        setDragOffset(0);
      }
    }
  };

  const onTouchEnd = () => {
    // Always reset drag state
    const wasDragging = isDragging;
    const finalDragOffset = dragOffset;
    
    setIsDragging(false);
    setDragOffset(0);
    
    if (!touchStart || !touchEnd) {
      setTouchStart(null);
      setTouchEnd(null);
      return;
    }
    
    const distance = touchStart - touchEnd;
    const absDragOffset = Math.abs(finalDragOffset);
    
    // Only process swipe if actual dragging happened
    if (!wasDragging || absDragOffset < 10) {
      setTouchStart(null);
      setTouchEnd(null);
      return;
    }
    
    // For collapsed state
    if (!isExpanded) {
      // If dragged up beyond threshold, expand
      if (distance > 0 && absDragOffset > snapThreshold) {
        setOpenedByDrag(true); // Mark as opened by drag
        setIsExpanded(true);
        setIsTabVisible(true);
        
        // Reset openedByDrag after animation completes to allow smooth subsequent opens
        if (dragResetTimerRef.current) {
          clearTimeout(dragResetTimerRef.current);
        }
        dragResetTimerRef.current = setTimeout(() => {
          setOpenedByDrag(false);
        }, 100); // Short delay to prevent animation glitch
      }
    } 
    // For expanded state
    else {
      // If dragged down beyond threshold, collapse
      if (distance < 0 && absDragOffset > snapThreshold) {
        setOpenedByDrag(false);
        setIsExpanded(false);
        resetHideTimer();
      }
    }
    
    // Reset touch tracking
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Handle tab click (only if not dragging)
  const handleTabClick = (e) => {
    // Don't trigger if user was dragging
    if (isDragging || Math.abs(dragOffset) > 10) {
      return;
    }
    
    e.stopPropagation();
    
    if (!isExpanded) {
      setOpenedByDrag(false); // Mark as opened by click
      setIsExpanded(true);
      setIsTabVisible(true);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    }
  };

  // Handle close
  const handleClose = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    setIsExpanded(false);
    setIsDragging(false);
    setDragOffset(0);
    setOpenedByDrag(false);
    resetHideTimer();
    // Call external close handler if provided
    if (onClose) {
      onClose();
    }
  };

  // Always render the tab - users can access even if no depth notes yet
  // (They can use AI generation or view slide details)
  
  return (
    <>
      {/* Fullscreen Button moved to ViewerMain top controls */}

      {/* Backdrop Overlay - Show during drag or when expanded */}
      {(isExpanded || (isDragging && dragOffset > 0)) && (
        <div 
          className="fixed inset-0 z-[90] bg-black"
          style={{
            opacity: isDragging
              ? dragOffset > 0
                ? Math.min((dragOffset / window.innerHeight) * 0.6, 0.4) // Fade in while dragging up
                : Math.max(0.4 - ((-dragOffset / window.innerHeight) * 0.4), 0) // Fade out while dragging down
              : isExpanded 
                ? 0.4 
                : 0,
            transition: isDragging ? 'none' : 'opacity 0.3s ease-out',
            pointerEvents: isExpanded && !isDragging ? 'auto' : 'none'
          }}
          onClick={(e) => {
            if (isExpanded && !isDragging) {
              e.stopPropagation();
              handleClose(e);
            }
          }}
          onTouchEnd={(e) => {
            if (isExpanded && !isDragging) {
              e.stopPropagation();
              e.preventDefault();
              handleClose(e);
            }
          }}
        />
      )}
      
      {/* Permanent Access Indicator - Only in normal view when tab is hidden */}
      {!isExpanded && !isTabVisible && !isFullscreen && (
        <button
          className="fixed bottom-4 right-4 z-[95] bg-slate-900/95 backdrop-blur-sm text-white p-3 rounded-full shadow-xl hover:bg-slate-800 active:bg-slate-700 transition-all hover:scale-110 active:scale-95"
          onClick={(e) => {
            e.stopPropagation();
            setIsTabVisible(true);
            resetHideTimer();
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setIsTabVisible(true);
            resetHideTimer();
          }}
          style={{
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'rgba(255, 255, 255, 0.1)',
            minWidth: '48px',
            minHeight: '48px'
          }}
          aria-label="Show Speaker Notes"
        >
          <Layers className="w-5 h-5" />
        </button>
      )}
      
      {/* Bottom Tab (Collapsed State) - Only in normal view, not fullscreen */}
      {!isExpanded && !isFullscreen && (
        <div
          className={`fixed bottom-0 left-0 right-0 z-[95] ${
            !isDragging ? 'transition-transform duration-300' : ''
          } ${
            isTabVisible ? 'translate-y-0' : 'translate-y-full'
          }`}
          style={{
            touchAction: 'pan-y',
            WebkitOverflowScrolling: 'touch',
            opacity: isDragging && dragOffset > 0 ? 0 : 1,
            transition: isDragging ? 'opacity 0.1s' : undefined
          }}
        >
          <button
            ref={tabRef}
            className="w-full border-0 bg-transparent swipe-panel-tab"
            onTouchStart={onTouchStart}
            onTouchEnd={(e) => {
              onTouchEnd();
              // Also handle tap if no significant drag
              if (Math.abs(dragOffset) < 10) {
                handleTabClick(e);
              }
            }}
            onClick={handleTabClick}
            style={{ 
              cursor: 'pointer',
              touchAction: 'none',
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none',
              userSelect: 'none',
              WebkitTapHighlightColor: 'transparent',
              WebkitUserDrag: 'none'
            }}
            aria-label="Open Speaker Notes"
          >
            <div className="bg-slate-900 text-white py-3 px-6 rounded-t-2xl shadow-2xl w-full pointer-events-none">
              <div className="flex flex-col items-center gap-1.5">
                <div className="w-12 h-1.5 bg-slate-600 rounded-full"></div>
                <div className="flex items-center gap-2">
                  <ChevronUp className="w-5 h-5" />
                  <span className="font-semibold text-sm">Speaker Notes</span>
                </div>
              </div>
            </div>
          </button>
        </div>
      )}

      {/* Expanded Panel (Full Screen) - Show during drag or when expanded */}
      {(isExpanded || (isDragging && dragOffset > 0)) && (
        <div 
          ref={panelRef}
          className={`fixed inset-0 z-[92] bg-slate-900 text-white flex flex-col swipe-panel-content ${
            !isDragging && isExpanded && !openedByDrag ? 'animate-slide-up' : ''
          }`}
          style={{
            // When dragging up from collapsed: start from bottom and move up with tab
            // When dragging down from expanded: move down
            transform: isDragging 
              ? dragOffset > 0 
                ? `translate3d(0, calc(100% - ${dragOffset}px), 0)` // Start at 100% and move up as user drags
                : `translate3d(0, ${-dragOffset}px, 0)` // Dragging down from expanded
              : isExpanded 
                ? 'translate3d(0, 0, 0)' 
                : 'translate3d(0, 100%, 0)',
            transition: isDragging || openedByDrag ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            WebkitTransform: isDragging 
              ? dragOffset > 0 
                ? `translate3d(0, calc(100% - ${dragOffset}px), 0)`
                : `translate3d(0, ${-dragOffset}px, 0)`
              : isExpanded 
                ? 'translate3d(0, 0, 0)' 
                : 'translate3d(0, 100%, 0)',
            WebkitOverflowScrolling: 'touch',
            height: '100vh', // Full viewport height
            borderTopLeftRadius: '1rem',
            borderTopRightRadius: '1rem'
          }}
        >
          {/* Header with Close Button - Swipe down enabled here */}
          <div 
            className="flex flex-col bg-slate-900"
            style={{ 
              borderTopLeftRadius: '1rem',
              borderTopRightRadius: '1rem'
            }}
          >
            {/* Tab-like top section that appears during swipe */}
            {isDragging && dragOffset < 200 && (
              <div className="py-3 px-6">
                <div className="flex flex-col items-center gap-1.5">
                  <div className="w-12 h-1.5 bg-slate-600 rounded-full"></div>
                  <div className="flex items-center gap-2">
                    <ChevronUp className="w-5 h-5" />
                    <span className="font-semibold text-sm">Speaker Notes</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Normal header */}
            <div 
              className={`flex items-center justify-between p-4 pb-3 border-b border-slate-700 ${
                isDragging && dragOffset < 200 ? 'hidden' : ''
              }`}
              style={{ 
                minHeight: '60px'
              }}
            >
              <div 
                ref={headerRef}
                className="flex items-center gap-3 flex-1"
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                style={{ 
                  cursor: 'grab',
                  touchAction: 'none',
                  WebkitTouchCallout: 'none',
                  paddingRight: '8px'
                }}
              >
                <div className="w-12 h-1.5 bg-slate-600 rounded-full"></div>
                <h3 className="text-lg font-semibold">Speaker Notes & Details</h3>
              </div>
              <Button
                onClick={handleClose}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleClose(e);
                }}
                variant="ghost"
                size="lg"
                className="rounded-full text-white hover:bg-slate-800 active:bg-slate-700 min-w-[48px] min-h-[48px] p-2 flex-shrink-0"
                style={{
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'rgba(255, 255, 255, 0.1)'
                }}
              >
                <X className="w-6 h-6" />
              </Button>
            </div>
          </div>

          {/* Scrollable Content - No swipe down here to allow scrolling */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Slide Preview */}
            {presentation?.pdf_data && slide?.pdf_page_number > 0 ? (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-slate-300 mb-3">Current Slide</h4>
                <div className="bg-white rounded-lg overflow-hidden border-2 border-slate-700 shadow-xl" style={{ maxHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PDFSlideViewer 
                    pdfData={presentation.pdf_data}
                    pageNumber={slide.pdf_page_number}
                    width={400}
                    maintainAspectRatio={true}
                  />
                </div>
              </div>
            ) : (
              <div 
                className="mb-6 rounded-lg p-6 border border-slate-700"
                style={{
                  background: slide?.background?.type === 'color' 
                    ? slide.background.value 
                    : slide?.background?.type === 'gradient'
                    ? slide.background.value
                    : slide?.background?.type === 'image'
                    ? `url(${slide.background.value}) center/cover`
                    : '#1e293b'
                }}
              >
                <h4 className="text-sm font-semibold text-slate-300 mb-2">Current Slide</h4>
                <div 
                  className="text-2xl font-bold mb-3"
                  style={{ color: 'initial' }}
                  dangerouslySetInnerHTML={{ __html: slide?.title || '' }}
                />
                {slide?.content && (
                  <div 
                    className="text-sm"
                    style={{ color: 'initial' }}
                    dangerouslySetInnerHTML={{ __html: slide.content }}
                  />
                )}
              </div>
            )}

            {/* Depth Notes */}
            <div>
              <h4 className="text-sm font-semibold text-slate-400 mb-2">SPEAKER NOTES</h4>
              {slide?.depth_notes ? (
                <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                  <p className="text-slate-200 whitespace-pre-wrap leading-relaxed">{slide.depth_notes}</p>
                </div>
              ) : (
                <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                  <p className="text-slate-400 text-sm italic">No speaker notes available for this slide.</p>
                  <p className="text-slate-500 text-xs mt-2">Speaker notes can be added in the Presenter Dashboard.</p>
                </div>
              )}
            </div>

            {/* Additional Resources */}
            {slide?.additional_resources && (
              <div>
                <h4 className="text-sm font-semibold text-slate-400 mb-2">ADDITIONAL RESOURCES</h4>
                <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                  <div className="space-y-2">
                    {slide.additional_resources.split('\n').filter(line => line.trim()).map((resource, idx) => {
                      const isUrl = resource.trim().match(/^https?:\/\//i);
                      const isImageUrl = isUrl && resource.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                      
                      if (isImageUrl) {
                        return (
                          <div key={idx} className="mb-3">
                            <img 
                              src={resource.trim()} 
                              alt="Resource" 
                              className="rounded-lg max-w-full h-auto border border-slate-600"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        );
                      } else if (isUrl) {
                        return (
                          <a 
                            key={idx}
                            href={resource.trim()} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300 underline block"
                          >
                            {resource.trim()}
                          </a>
                        );
                      } else {
                        return (
                          <p key={idx} className="text-slate-300">
                            {resource}
                          </p>
                        );
                      }
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer hint - Swipe down enabled here */}
          <div 
            ref={footerRef}
            className="p-4 bg-slate-800/50 text-center"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            style={{ 
              cursor: 'grab',
              touchAction: 'none',
              WebkitTouchCallout: 'none',
              minHeight: '50px'
            }}
          >
            <div className="w-12 h-1 bg-slate-600 rounded-full mx-auto mb-2"></div>
            <p className="text-xs text-slate-400">Swipe down or tap ✕ to close</p>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        
        .animate-slide-up {
          animation: slide-up 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        /* Safari-specific fixes */
        @supports (-webkit-touch-callout: none) {
          .swipe-panel-tab {
            -webkit-transform: translateZ(0);
            transform: translateZ(0);
            -webkit-backface-visibility: hidden;
            backface-visibility: hidden;
            will-change: transform;
          }
          
          .swipe-panel-content {
            -webkit-overflow-scrolling: touch;
            -webkit-transform: translateZ(0);
            transform: translateZ(0);
          }
        }
      `}</style>
    </>
  );
};

export default SwipeUpPanel;
